import { Box, Button, ButtonGroup, Tooltip, Typography, styled, tooltipClasses } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import LockSectionIcon from '../../assets/lockSectionIcon.png';
import { ReactComponent as PulseInfoWhiteIcon } from '../../assets/pulse-info-icon-white.svg';
import {
  AutomatedInsightSessionType,
  AutomatedInsightSessionTypeToDisplayName,
} from '../../modules/automated-insights/constants';
import { Paths } from '../../routes';
import FeaturesList from './FeaturesList';
import CustomTooltip from './ui/Tooltip';

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  border: '1px solid rgba(217, 217, 217, 0.2)',
  borderRadius: '10px',
  '& .MuiButton-root': {
    flex: 1, // Ensure all buttons take up equal space
    whiteSpace: 'nowrap',
  },
}));

const DisableFeaturesTooltip = styled(({ className, maxWidth, height, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth, height }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    fontSize: '12px',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    height: height || '140px',
    minWidth: '340px',
  },
}));

const ExploreButton = styled(Button)(({ theme }) => ({
  height: '28px',
  width: '105px',
  borderRadius: '27px',
  border: '1px solid #082739',
  color: 'rgba(9, 41, 60, 1)',
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  '&:hover': {
    border: '1px solid #082739',
  },
}));

const styles = {
  button: {
    minHeight: 34,
    paddingX: 4,
    textTransform: 'none',
    border: 'none',
    '&:hover': {
      border: 'none',
    },
  },
};

const InfoIconWithFeatures = ({ type }) => {
  const getTooltipContent = (type) => {
    return (
      <Box py={1}>
        <FeaturesList sessionType={type} />
      </Box>
    );
  };

  return (
    <CustomTooltip title={getTooltipContent(type)} placement="bottom">
      <Box display="flex" alignItems="center">
        <PulseInfoWhiteIcon />
      </Box>
    </CustomTooltip>
  );
};

const SessionTypeSelection = ({ sessionType, onSelection, background, typesLocked = [] }) => {
  const history = useHistory();
  const { track } = useAnalytics();

  const experienceLocked = useMemo(
    () => typesLocked?.includes(AutomatedInsightSessionType.Experience),
    [typesLocked]
  );

  const handleButtonClick = (type) => {
    if (typesLocked?.includes(type)) {
      return;
    }

    onSelection(type);
  };

  const redirectToSubscriptions = useCallback(() => {
    track(TrackEvent.CLICKED_BUTTON_TO_EXPLORE_PLANS_FROM_BLOCKED_FEATURE);
    history.push(`${Paths.automatedInsights.basePath}/subscription`);
  }, [history, track]);

  const getTooltipContent = (type) => {
    if (typesLocked?.includes(type) && type === AutomatedInsightSessionType.Experience) {
      return (
        <Box sx={{ marginTop: -1 }}>
          <Box sx={{ marginLeft: 1, marginTop: 2, marginBottom: 2 }}>
            <Typography variant="body1" sx={{ fontSize: '13px' }}>
              <b>Feature not supported</b>
            </Typography>
          </Box>
          <Box sx={{ marginLeft: 1, marginTop: -1 }}>
            <Typography variant="body1" sx={{ fontSize: '13px' }}>
              Your current subscription does not support Journeys.
            </Typography>
          </Box>
          <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
            <ExploreButton onClick={redirectToSubscriptions}>Explore plans</ExploreButton>
          </Box>
        </Box>
      );
    }
    return '';
  };

  return (
    <StyledButtonGroup aria-label="session type buttons" disableElevation>
      <Button
        onClick={() => handleButtonClick(AutomatedInsightSessionType.Page)}
        endIcon={<InfoIconWithFeatures type={AutomatedInsightSessionType.Page} />}
        sx={{
          ...styles.button,
          borderTopLeftRadius: '9px',
          borderBottomLeftRadius: '9px',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          fontSize: { xs: 11, sm: 12 },
          fontWeight: sessionType === AutomatedInsightSessionType.Page ? 600 : 400,
          background: (theme) =>
            sessionType === AutomatedInsightSessionType.Page
              ? background || theme.palette.gradient.main
              : 'transparent',
        }}>
        {AutomatedInsightSessionTypeToDisplayName[AutomatedInsightSessionType.Page]}
      </Button>
      <Button
        onClick={() => handleButtonClick(AutomatedInsightSessionType.Experience)}
        endIcon={
          <>
            <InfoIconWithFeatures type={AutomatedInsightSessionType.Experience} />
            {experienceLocked && (
              <DisableFeaturesTooltip title={getTooltipContent(AutomatedInsightSessionType.Experience)}>
                <img src={LockSectionIcon} alt="lock" style={{ marginLeft: '8px', width: '11px' }} />
              </DisableFeaturesTooltip>
            )}
          </>
        }
        sx={{
          ...styles.button,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderTopRightRadius: '9px',
          borderBottomRightRadius: '9px',
          borderLeft: '1px solid rgba(217, 217, 217, 0.2)',
          fontSize: { xs: 11, sm: 12 },
          fontWeight: sessionType === AutomatedInsightSessionType.Experience ? 600 : 400,
          background: (theme) =>
            sessionType === AutomatedInsightSessionType.Experience
              ? background || theme.palette.gradient.main
              : 'transparent',
          cursor: experienceLocked ? 'not-allowed' : 'pointer',
          '&:hover': {
            border: 'none',
            borderLeft: '1px solid rgba(217, 217, 217, 0.2)',
          },
        }}>
        {AutomatedInsightSessionTypeToDisplayName[AutomatedInsightSessionType.Experience]}
      </Button>
    </StyledButtonGroup>
  );
};

export default SessionTypeSelection;
